import { useEffect, useRef, useState } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { LiveRegion } from "components/live-region";

/**
 * Route-change focus + announcement for the SPA (WCAG 2.4.3 / status message).
 *
 * On a fresh page load the browser already sets focus and the AT reads the
 * document, so we skip the first mount. On every subsequent client-side
 * navigation (pathname change only — not query/hash) we:
 *   1. Move keyboard focus to the page's `<main id="main-content">` so
 *      keyboard/screen-reader users land on the new content instead of a stale
 *      focus position left on the link they activated. `preventScroll` is
 *      essential here: focusing an element scrolls it into view, and `<main>`
 *      starts below the fixed app bar, so a plain `focus()` scrolled every
 *      navigation down by the bar's height (measured: 0 → 65px on
 *      `/dashboard`). Every screen with a scrollbar therefore rendered, then
 *      visibly jumped down a moment later and had to be scrolled back up.
 *      `ScrollToTop` has already put the page at the top by this point, which
 *      is where the new screen belongs.
 *   2. Announce the new `document.title` (set by each screen's `useTitle`)
 *      through the polite global live region.
 *
 * The short timeout lets the destination screen mount its `<main>` and run its
 * `useTitle` effect before we read the title / focus the landmark.
 */
const RouteAnnouncerComponent = ({ location }: RouteComponentProps) => {
  const [announcement, setAnnouncement] = useState<string>("");
  const mounted = useRef(false);

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }

    const timeoutId = window.setTimeout(() => {
      const target =
        document.querySelector<HTMLElement>("[data-route-focus]") ??
        document.getElementById("main-content");
      if (target) {
        target.focus({ preventScroll: true });
      }
      setAnnouncement(document.title);
    }, 150);

    return () => window.clearTimeout(timeoutId);
  }, [location.pathname]);

  return (
    <LiveRegion id="app-live-region" message={announcement} priority="polite" />
  );
};

export const RouteAnnouncer = withRouter(RouteAnnouncerComponent);
